import _ from 'lodash';
import { TableViewStore, BaseListViewStore } from "core/stores";
import { TrackingCaseDocumentService, TrackingCaseDocumentStreamService, TrackingCaseService } from "application/tracking-case/services";
import { action, observable } from "mobx";
import { ModalParams } from 'core/models';
import { toDate } from 'core/utils';

class TrackingCaseDocumentListViewStore extends BaseListViewStore {
    @observable accessor trackingCaseName = null;

    constructor(rootStore) {
        const id = rootStore.routerStore.routerState.params.id;

        const service = new TrackingCaseDocumentService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            autoInit: true,
            routes: {
                fieldValues: id => {
					this.rootStore.routerStore.goTo("master.app.main.tracking-case.documents.field-values", { id: id });
				}
            },  
            actions: () => {
                return {
                    find: async params => {
                        const response = await service.find({...params, trackingCaseId: id, embed: 'documentType,documentForm,coreMediaVaultEntry'});

                        response.data.item.forEach(item => {
                            item.validFrom = item.validFrom && toDate(item.validFrom);
                            item.validTo = item.validTo && toDate(item.validTo);
                            item.dateCreated =  item.dateCreated && toDate(item.dateCreated);

                            if(!item.coreMediaVaultEntry) {
                                item.coreMediaVaultEntry = {
                                    fileName: '',
                                    fileExtension: '',
                                    fileSize: ''
                                };
                                item.coreMediaVaultEntry.coreUser = {userName:''};
                            }
                            else{
                                item.coreMediaVaultEntry.fileSize = `${item.coreMediaVaultEntry.fileSize} Kb`;
                            }

                            if(!item.documentType){
                                item.documentType = {name: ''};
                            }

                            if(!item.documentForm){
                                item.documentForm = {name: ''};
                            }

                            item.status = item.isActive ? "Active" : "Deleted";
                        });

                        return response.data;
                    },
                    submit: async resource => {
                        const response = await service.submit(resource);
                        return response.data;
                    },
                    delete: service.delete,
                    batchDelete: service.batchDelete
                };
            }
        });

        this.setTableStore(
            new TableViewStore(this.queryUtility, {
                columns: [
                    {
                        key: "name",
                        title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.NAME"
                    },
                    {
                        key: "documentType.name",
                        title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.DOCUMENT_TYPE",
						sortable:false
                    },
                    {
                        key: "documentForm.name",
                        title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.DOCUMENT_FORM",
						sortable:false
                    },
                    {
                        key: "description",
                        title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.DESCRIPTION"
                    },
                    {
                        key: "status",
                        title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.STATUS"
                    },
                    {
                        key: "validFrom",
                        title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.VALID_FROM"
                    },
                    {
                        key: "validTo",
                        title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.VALID_TO",
                    },
                    {
                        key: "dateCreated",
                        title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.DATE_CREATED"
                    },
                    {
                        key: "coreMediaVaultEntry.coreUser.userName",
                        title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.ADDED_BY",
						sortable:false
                    },
                    // {
                    //     key: "coreMediaVaultEntry.fileName",
                    //     title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.FILENAME",
					// 	sortable:false
                    // },
                    // {
                    //     key: "coreMediaVaultEntry.fileExtension",
                    //     title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.FILE_TYPE",
					// 	sortable:false
                    // },
                    // {
                    //     key: "coreMediaVaultEntry.fileSize",
                    //     title: "TRACKING_CASE_DOCUMENT.LIST.COLUMNS.FILESIZE",
					// 	sortable:false
                    // }
                ],
                actions: {
					onDocumentView: item => this.onDocumentView(item.id),
					onFieldValues: item => this.routes.fieldValues(item.id),
                    onSubmit: item => this.onSubmit(item),
                    onPreview: item => this.onPreview(item),
                    onDownload: item =>this.onDownload(item),
                    // onEdit: item => this.routes.openEditView(item.id),
                    onDelete: item => this.deleteResource(item),
                }
            })
        );

        this.id = id;
        this.uploadModal = new ModalParams({});
        this.editModal = new ModalParams({});

        this.trackingCaseService = new TrackingCaseService(rootStore.application.baasic.apiClient);
        this.streamService = new TrackingCaseDocumentStreamService(rootStore.application.baasic.apiClient);
        this.apiDownloadEndpoint =`${this.streamService.apiClient.appOptions.apiUrl}/${this.streamService.routeService.base}`;

        this.getTrackingCaseName();
    }

    @action.bound
    async getTrackingCaseName() {
        var response = await this.trackingCaseService.get(this.id, { embed: "patient,facility" });
        if(response.data) {
            var validTo = response.data.validTo ?  ` - ${toDate(response.data.validTo)}` : '';
            this.trackingCaseName = `${response.data.patient.lastName} ${response.data.patient.firstName} (${response.data.facility.name}) | ${toDate(response.data.validFrom)}${validTo}`;
        }
    }

    @action.bound
    openUploadPopup() {
        this.uploadModal.open({
            trackingCaseId: this.id,
            fetchTable: () => {
                this.queryUtility.fetch();
            }
        });
    }

    @action.bound
    openEditPopup() {
        this.editModal.open({
            trackingCaseId: this.id,
            fetchTable: () => {
                this.queryUtility.fetch();
            }
        });
    }

    @action.bound
    onSubmit(resource) {
        if (!this.actions.submit) return;

        const { modalStore, notificationStore } = this.rootStore;

        modalStore.showConfirm(
            'TRACKING_CASE_DOCUMENT.SUBMIT.CONFIRM_SUBMIT',
            async () => {
                try {
                    this.actions.submit({id: resource.id});
                    notificationStore.success('TRACKING_CASE_DOCUMENT.SUBMIT.SUCCESS_SUBMIT');
                    await this.queryUtility.fetch();
                } catch (err) {
                    notificationStore.error('TRACKING_CASE_DOCUMENT.SUBMIT.ERROR_SUBMIT', err);
                }
            }
        );
    }

    @action.bound
    async onDocumentView(id) {
        let url = (`${this.rootStore.routerStore.findRoute('master.app.main.tracking-case.documents.field-values-edit').pattern}`).replace(":trackingCaseDocumentId", id);
        window.open(url);
    }
    
    @action.bound
    async onPreview(clientAttachment) {
        window.open(`${this.apiDownloadEndpoint}/${clientAttachment.id}?contentDisposition=inline`);
    }

    @action.bound
    async onDownload(clientAttachment) {
        window.open(`${this.apiDownloadEndpoint}/${clientAttachment.id}?contentDisposition=attachment`); // This shortly open file in new window/tab. Pros about this is if file is large, instantly start downloading, instead of next solution where file is started downloading when backend returned 100%
    }

    @action.bound
    async goBack() {
        this.rootStore.routerStore.goBack();
    }

    @action.bound
    deleteResource(resource) {
        if (!this.actions.find || !this.actions.delete) return;

        const { modalStore, notificationStore } = this.rootStore;

        modalStore.showConfirm(
            'TRACKING_CASE_DOCUMENT.DELETE.CONFIRM_DELETE',
            async () => {
                try {
                    if (_.isArray(resource)) {
                        await this.actions.batchDelete(resource);
                    }
                    else {
                        await this.actions.delete({id: resource.id});
                    }
                    notificationStore.success('TRACKING_CASE_DOCUMENT.DELETE.SUCCESS_DELETE');
                    await this.queryUtility.fetch();
                } catch (err) {
                    notificationStore.error(err.data && typeof err.data !== "object" ? err.data: 'TRACKING_CASE_DOCUMENT.DELETE.ERROR_DELETE', err);
                }
            }
        );
    }
}

export default TrackingCaseDocumentListViewStore;